import { useMutation } from "@shared/lib/hooks";
import { ToastService } from "@shared/lib/services";
import { BackednResponse } from "@shared/lib/types";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { AI_WORKSPACE_QUERY_KEYS } from "../constants";
import { AiWorkspaceService } from "../services/aiWorkspace.service";
import {
  ICreatorScriptGeneratorJobDetail,
  ISaveCreatorScriptPayload,
} from "../types";

export function useSaveCreatorScript() {
  const saveCreatorScript = useCallback(
    (payload: ISaveCreatorScriptPayload) =>
      AiWorkspaceService.saveCreatorScript(payload).catch(async (err) => {
        let errorMessage = "Failed to save script";
        if (err?.response) {
          try {
            const responseBody = await err.response.json();
            if (responseBody?.msg) errorMessage = responseBody.msg;
          } catch (e) {}
        }
        ToastService.error(errorMessage);
        throw err;
      }),
    []
  );

  const queryClient = useQueryClient();
  const { mutate: save, isLoading: isSaving } = useMutation<
    ISaveCreatorScriptPayload,
    BackednResponse<ICreatorScriptGeneratorJobDetail>
  >({
    mutationFn: saveCreatorScript,
    options: { skipInvalidation: true },
    onSuccess: (client) => {
      client.invalidateQueries({
        queryKey: [AI_WORKSPACE_QUERY_KEYS.CREATOR_SCRIPT_JOB],
      });
    },
  });

  const saveNow = async (payload: ISaveCreatorScriptPayload) => {
    const saved = await saveCreatorScript(payload);
    queryClient.setQueryData(
      [AI_WORKSPACE_QUERY_KEYS.CREATOR_SCRIPT_JOB, payload.creatorScriptGeneratorJobId],
      (current: BackednResponse<ICreatorScriptGeneratorJobDetail> | undefined) => {
        if (!current?.data) return saved;
        return {
          ...current,
          data: { ...current.data, ...saved?.data, script: payload.script },
        };
      }
    );
    return saved;
  };

  return { save, saveNow, isSaving };
}
